<template>
  <div class="simple-drop-down">
    <!--BUTTON-->
    <div class="simple-drop-down__button"
         ref="button"
         @click.prevent="_onButtonClickHandler"
    >
      <slot name="button"
            :optionsListActive="state.active"
      />
    </div>

    <!--LIST-->
    <transition name="vue-animation-fade">
      <div v-show="state.active"
           ref="options"
           class="simple-drop-down__options simple-drop-down__options__map_navbar"
           :class="optionsListClass"
           :style="[
             state.dropdownList.position
           ]"
      >
        <div class="simple-drop-down__options__option"
             v-for="(option, index) in optionsList"
             :style="optionStyle"
             :class="[
								{
									active: _comparator(option, value),
									'simple-drop-down__options__option--first':
									  !state.isOptionsListOverflow && index === 0,
									'simple-drop-down__options__option--last':
									  !state.isOptionsListOverflow && (index === options.length - 1) && !actions.length,
									'simple-drop-down__options__option--disabled': !!option.$isDisabled
								},
								optionClass
						  ]"
             :key="_getUniqueKey(option)"
             @click.prevent="_onSelectHandler(option)"
        >
          <slot name="option"
                :option="option"
          />
        </div>

        <div v-for="(action, $actIdx) in actions"
             :key="$actIdx"
             class="simple-drop-down__options__action"
             :class="{
               'simple-drop-down__delimiter': !$actIdx,
               'simple-drop-down__options__action--last':
                  $actIdx === actions.length - 1
             }"
             @click="_actionClickHandler(action)"
        >
          <slot name="action"
                :action="action"
          >
            <span>{{ action.description }}</span>
          </slot>
        </div>
      </div>
    </transition>
  </div>
</template>

<script lang="js">
import { OPTIONS_LIST_OFFSET } from './config';

export default {
  name: 'vue-simple-drop-down',

  model: {
    event: 'select',
    prop: 'value'
  },

  props: {
    value: {
      type: [String, Number, Object, Array],
      required: false,
      default: null
    },
    options: {
      type: Array,
      required: false,
      default: () => []
    },
    actions: {
      type: Array,
      required: false,
      default: () => []
    },
    trackBy: {
      type: String,
      required: false,
      default: null
    },
    handleClickOnSelected: {
      type: Boolean,
      required: false,
      default: false
    },
    closeOnSelect: {
      type: Boolean,
      required: false,
      default: true
    },
    closeOnSelectDisabled: {
      type: Boolean,
      required: false,
      default: true
    },
    selectedFirst: {
      type: Boolean,
      required: false,
      default: true
    },
    offset: {
      type: Object,
      required: false,
      default: () => ({})
    },
    optionsListClass: {
      type: [String, Object],
      required: false,
      default: ''
    },
    optionClass: {
      type: [String, Object],
      required: false,
      default: ''
    },
    optionStyle: {
      type: [String, Object],
      required: false,
      default: ''
    }
  },

  data() {
    return {
      state: {
        isOptionsListOverflow: false,
        active: false,
        dropdownList: {
          value: null,

          position: {
            top: 0,
            left: 0
          },
          offset: {
            ...OPTIONS_LIST_OFFSET,
            ...this.offset
          }
        }
      }
    };
  },

  computed: {
    /**
     * @return {*[]}
     */
    optionsList() {
      if ( this.selectedFirst ) {
        return this.options;
      }

      const selectedIndex = this.options.findIndex(
        option => this._comparator(option, this.value)
      );

      let result = this.options;

      if ( selectedIndex !== -1 ) {
        result.unshift(this.options[selectedIndex]);
        result.splice(selectedIndex + 1, 1);
      }

      return result;
    }
  },

  watch: {
    /**
     * @param {boolean} active
     */
    'state.active'(active) {
      if ( !active ) {
        window.setTimeout(
          () => this.$refs.options.scrollTo({ top: 0 }),
          200
        );

        window.document.body.removeEventListener(
          'click',
          this._onClickOutsideHandler
        );

        return;
      }

      window.document.body.addEventListener(
        'click',
        this._onClickOutsideHandler
      );

      this.$nextTick(() => {
        this.state.dropdownList.position = this._computeDropdownListPosition();
        this._checkOptionsListOverflow();
      });
    }
  },

  mounted() {
    this.$nextTick(() => {
      this.state.dropdownList.position = this._computeDropdownListPosition();
      this._checkOptionsListOverflow();
    });
  },

  beforeDestroy() {
    window.document.body.removeEventListener(
      'click',
      this._onClickOutsideHandler
    );
  },

  methods: {
    open() {
      this.state.active = true;
    },

    close() {
      this.state.active = false;
    },

    /**
     * @param {*} action
     */
    _actionClickHandler(action) {
      if (
        (action?.$closeOnClicked ?? true)
      ) {
        this.state.active = false;
      }

      action?.onclick();
    },

    /**
     * @returns {void}
     */
    _checkOptionsListOverflow() {
      this.state.isOptionsListOverflow = !this.$refs.options
        ? false
        : (this.$refs.options.scrollHeight > this.$refs.options.clientHeight);
    },

    /**
     * @return {{top: string, left: string}}
     */
    _computeDropdownListPosition() {
      const element = /** @type {HTMLElement} */ this.$refs.button;
      const { dropdownList } = this.state;
      let dropdownListOffset = {};

      Object.entries(dropdownList.offset).forEach(
        ([key, val]) => {
          dropdownListOffset[key] = typeof val === 'number' ?
            `${ val }px` :
            val;
        }
      );

      return {
        top: `calc(${ element.offsetTop }px + ${ dropdownListOffset.top })`,
        left: `calc(${ element.offsetLeft }px + ${ dropdownListOffset.left })`
      };
    },

    /**
     * @param {*} option
     * @return {*}
     */
    _getUniqueKey(option) {
      return this.trackBy ? option[this.trackBy] : option;
    },

    /**
     * @param {*} valueA
     * @param {*} valueB
     * @return {boolean}
     */
    _comparator(valueA, valueB) {
      if ( (typeof valueA === 'undefined') || (typeof valueB === 'undefined') ) {
        return false;
      }

      return (this.trackBy && valueA !== null && valueB !== null) ?
        valueA[this.trackBy] === valueB[this.trackBy] :
        valueA === valueB;
    },

    /**
     * @param {*} option
     */
    _onSelectHandler(option) {
      if ( this._comparator(option, this.value) && !this.handleClickOnSelected ) {
        return;
      }

      if (
        (option.$isDisabled && this.closeOnSelectDisabled) ||
        (!option.$isDisabled && this.closeOnSelect)
      ) {
        this.state.active = false;
      }

      if ( typeof option?.onClick === 'function' ) {
        option.onClick(option);
      }

      this.$emit('select', option);
    },

    /**
     * @param {PointerEvent} e
     */
    _onButtonClickHandler(e) {
      this.state.active = !this.state.active;
    },

    /**
     * @param {PointerEvent} e
     */
    _onClickOutsideHandler(e) {
      if ( this.state.active ) {
        const item = (e.path || (e.composedPath && e.composedPath()) || []).find(item => item === this.$el);

        if ( !item ) {
          this.state.active = false;
        }
      }
    }
  }
};
</script>
